import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MailIcon } from 'lucide-react';
import { useAccount, useSignedInAccount } from '../../contexts/AccountContext';
import { demoVerifyLink } from '../../utils/accountApi';

/** Zo lang wacht "Stuur opnieuw" na een verstuurde mail; de server begrenst het ook (429). */
const COOLDOWN_S = 60;

/**
 * Bovenaan Mijn omgeving zolang het e-mailadres niet bevestigd is. Rustig, geen blokkade:
 * alles werkt, alleen het koppelen van eerdere bestellingen op dit adres wacht erop.
 */
export function VerifyEmailBanner() {
  const {
    account
  } = useSignedInAccount();
  const {
    resendVerification
  } = useAccount();
  const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [wait, setWait] = useState(0);
  useEffect(() => {
    if (wait <= 0) return;
    const timer = window.setTimeout(() => setWait(w => w - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [wait]);

  // `=== false`: oudere gegevens zonder dit veld gelden als bevestigd.
  if (account.customer.emailVerified !== false) return null;
  const resend = async () => {
    setState('busy');
    setError(null);
    const res = await resendVerification();
    if (res.ok) {
      setState('sent');
      setWait(COOLDOWN_S);
    } else {
      setState('idle');
      setError(res.error);
    }
  };

  // DEMO: de link die anders in de mail staat. Met een backend is demoVerifyLink null.
  const demoLink = demoVerifyLink?.() ?? null;
  return <section aria-labelledby="verify-title" className="mb-8 flex gap-4 bg-shell p-5 ring-1 ring-line sm:p-6" data-id="element-6641" data-mp="3dkc1ftsl7jux">
      <MailIcon className="mt-0.5 h-5 w-5 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6642" data-mp="38omz5ip6noux" />
      <div className="min-w-0" data-id="element-6643" data-mp="34n1rqcvf42s4">
        <h2 id="verify-title" className="text-[15px] font-medium text-ink" data-id="element-6644" data-mp="16uxiwzbyq4us">
          Bevestig je e-mailadres
        </h2>
        <p className="mt-1 text-[14px] leading-relaxed text-muted" data-id="element-6645" data-mp="12una7ewsd0c8">
          We stuurden een link naar <span className="break-all text-ink" data-id="element-6646" data-mp="2wc2u8khbevt9">{account.customer.email}</span>. Klik erop, dan koppelen we ook eerdere bestellingen op dit adres aan je account.
        </p>
        <p role="status" className="mt-2 text-[14px] text-sage" data-id="element-6647" data-mp="2nbwhj0tfxzv5">
          {state === 'sent' ? 'Verstuurd. Niet gezien? Kijk even bij spam.' : ''}
        </p>
        {error && <p role="alert" className="mt-2 text-[14px] text-clay" data-id="element-6648" data-mp="2ravi69853ags">
            {error}
          </p>}
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-[14px]" data-id="element-6649" data-mp="3k1x20nms1u6n">
          <button type="button" onClick={() => void resend()} disabled={state === 'busy' || wait > 0} className="py-2 text-ink underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay disabled:text-muted disabled:no-underline" data-id="element-6650" data-mp="3jzja2nne8y3b">
            {state === 'busy' ? 'Versturen…' : wait > 0 ? `Opnieuw sturen kan over ${wait} s` : 'Stuur de link opnieuw'}
          </button>
          <Link to="/account/gegevens" className="py-2 text-muted underline-offset-4 hover:text-ink hover:underline" data-id="element-6651" data-mp="1r403n3arbz2r">
            Ander adres invullen
          </Link>
          {demoLink &&
        // Gewone link (volledig laden): de bevestiging wordt bij het openen van Mijn omgeving verwerkt.
        <a href={demoLink} className="rounded-sm border border-dashed border-line px-2 py-1 text-[13px] text-muted hover:text-ink" data-id="element-6652" data-mp="1w5fsz7jywrqy">
              Demo: open de link uit de mail
            </a>}
        </div>
      </div>
    </section>;
}